/**
 * Pulse launcher pill — T-311.
 *
 * Vanilla DOM/CSS implementation of the first-principles launcher
 * design (stamped 2026-05-01):
 *
 *   - 64px circle, ink ground, silver ring, `M` letter wordmark
 *   - Sonar pulse every 4s (idle), pause on hover
 *   - Hover scale 1.05x + tooltip ("Try them on")
 *   - Auto-tease at 8s of idle: tooltip flashes 1.2s, one-shot
 *   - Reduced-motion drops the pulse and entrance animation
 *
 * Two states tied to outer health gate:
 *   - "available"   — normal, click → onClick callback (open widget)
 *   - "unavailable" — greyed out, click → "catching our breath" toast
 *
 * No React; CSS owns the pulse + entrance + hover transitions.
 * JS handles only state transitions (hover, tease, gate state) and
 * the click → callback wiring.
 */

import type { HealthState } from "./healthProbe";
import { injectStylesheet } from "./styles";

const TEASE_DELAY_MS = 8000;
const TEASE_DURATION_MS = 1200;
const TOOLTIP_GAP_PX = 12;

export interface LauncherHandle {
  setState(state: HealthState): void;
  destroy(): void;
}

export function mountLauncher(onOpen: () => void): LauncherHandle {
  injectStylesheet();

  const btn = document.createElement("button");
  btn.id = "muse-noir-launcher";
  btn.type = "button";
  btn.setAttribute("aria-label", "Open Muse virtual try-on");
  btn.setAttribute("data-state", "available");
  btn.innerHTML = `<span class="muse-noir-mark" aria-hidden="true">M</span>`;
  document.body.appendChild(btn);

  const tooltip = document.createElement("div");
  tooltip.id = "muse-noir-tooltip";
  tooltip.setAttribute("role", "tooltip");
  tooltip.textContent = "Try them on";
  document.body.appendChild(tooltip);

  let currentState: HealthState = "available";
  let teaseFiredOnce = false;
  let teaseTimer: number | null = null;
  let interactionRecorded = false;

  function positionTooltip() {
    const rect = btn.getBoundingClientRect();
    // Center horizontally above the pill, gap above.
    const tooltipRect = tooltip.getBoundingClientRect();
    const top = rect.top - tooltipRect.height - TOOLTIP_GAP_PX;
    const left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    tooltip.style.top = `${Math.max(8, top)}px`;
    tooltip.style.left = `${Math.max(8, left)}px`;
  }

  function setTooltipText(text: string) {
    tooltip.textContent = text;
  }

  function showTooltip(mode: "hover" | "tease" = "hover") {
    if (currentState === "unavailable") {
      setTooltipText("We're catching our breath. Try in a moment.");
    } else {
      setTooltipText("Try them on");
    }
    tooltip.setAttribute("data-mode", mode);
    positionTooltip();
    tooltip.setAttribute("data-visible", "true");
  }

  function hideTooltip() {
    tooltip.setAttribute("data-visible", "false");
    tooltip.removeAttribute("data-mode");
  }

  function recordInteraction() {
    if (interactionRecorded) return;
    interactionRecorded = true;
    if (teaseTimer !== null) {
      window.clearTimeout(teaseTimer);
      teaseTimer = null;
    }
  }

  function scheduleTease() {
    if (teaseFiredOnce) return;
    teaseTimer = window.setTimeout(() => {
      teaseTimer = null;
      if (interactionRecorded) return;
      if (currentState === "unavailable") return;
      teaseFiredOnce = true;
      showTooltip("tease");
      // Tease auto-fades via CSS animation (1200ms forwards keyframes
      // end at opacity 0). Clean the data attribute after the fade.
      window.setTimeout(() => {
        if (tooltip.getAttribute("data-mode") === "tease") {
          hideTooltip();
        }
      }, TEASE_DURATION_MS + 100);
    }, TEASE_DELAY_MS);
  }

  function onMouseEnter() {
    recordInteraction();
    showTooltip("hover");
  }
  function onMouseLeave() {
    if (tooltip.getAttribute("data-mode") === "tease") return;
    hideTooltip();
  }
  function onFocus() {
    recordInteraction();
    showTooltip("hover");
  }
  function onBlur() {
    hideTooltip();
  }
  function onClick() {
    recordInteraction();
    if (currentState === "unavailable") {
      // Show the unavailable tooltip but don't open the widget.
      showTooltip("hover");
      window.setTimeout(hideTooltip, 2400);
      return;
    }
    onOpen();
  }
  function onResize() {
    if (tooltip.getAttribute("data-visible") === "true") {
      positionTooltip();
    }
  }

  btn.addEventListener("mouseenter", onMouseEnter);
  btn.addEventListener("mouseleave", onMouseLeave);
  btn.addEventListener("focus", onFocus);
  btn.addEventListener("blur", onBlur);
  btn.addEventListener("click", onClick);
  window.addEventListener("resize", onResize);
  window.addEventListener("scroll", onResize, { passive: true });

  scheduleTease();

  return {
    setState(state: HealthState) {
      currentState = state;
      btn.setAttribute("data-state", state);
    },
    destroy() {
      if (teaseTimer !== null) {
        window.clearTimeout(teaseTimer);
        teaseTimer = null;
      }
      btn.removeEventListener("mouseenter", onMouseEnter);
      btn.removeEventListener("mouseleave", onMouseLeave);
      btn.removeEventListener("focus", onFocus);
      btn.removeEventListener("blur", onBlur);
      btn.removeEventListener("click", onClick);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize);
      btn.remove();
      tooltip.remove();
    },
  };
}

export function showLauncher(): void {
  const btn = document.getElementById("muse-noir-launcher");
  if (btn) btn.style.display = "flex";
}

export function hideLauncher(): void {
  const btn = document.getElementById("muse-noir-launcher");
  if (btn) btn.style.display = "none";
  const tooltip = document.getElementById("muse-noir-tooltip");
  if (tooltip) tooltip.setAttribute("data-visible", "false");
}
